import React, { useState, useMemo } from 'react'

import { Check, Crown, Info } from 'lucide-react'

import { Subscription } from '@/core/api/entities/account/subscription'
import { SubscriptionPlan } from '@/core/api/entities/account/subscriptionPlan'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { cn } from '@/shared/utils/helpers'

type ModalStep = 'select' | 'confirm' | 'success'

interface Plan {
	id: string
	name: string
	description: string
	price: number
	currency: string
	features: string[]
	isCurrent?: boolean
	isHighlighted?: boolean
	isContactSales?: boolean
	tier?: number
}

interface UpgradePlanModalProps {
	visible: boolean
	onClose: () => void
	onUpgrade: (planId: string) => void
	subscription?: Subscription
	currentPlanId?: string
	currentPlanName?: string
	zonesCount?: number
	billingCycle?: string
	subscriptionPlans?: SubscriptionPlan[]
}

const PLANS: Plan[] = [
	{
		id: 'essential',
		name: 'Essential',
		description: 'For small to mid-size brands',
		price: 15,
		currency: 'USD',
		features: [
			'300+ playlists',
			'Zone monitoring',
			'Basic integrations',
			'25 AI ad credits',
		],
		isCurrent: true,
	},
	{
		id: 'advanced',
		name: 'Advanced',
		description: 'For multi-location brands',
		price: 30,
		currency: 'USD',
		features: [
			'Custom playlists',
			'Advanced integrations',
			'Ad Manager Pro',
			'50 AI ad credits',
			'Dedicated support',
		],
		isHighlighted: true,
	},
	{
		id: 'signature',
		name: 'Signature',
		description:
			'Usually very selective brands that seek an exclusive music service.',
		price: 0,
		currency: 'USD',
		features: [],
		isContactSales: true,
	},
]

const PlanCard: React.FC<{
	plan: Plan
	isSelected: boolean
	onSelect: () => void
}> = ({ plan, isSelected, onSelect }) => {
	const { t } = useTranslation('common')

	if (plan.isContactSales) {
		return (
			<div className="flex lg:flex-row flex-col items-center justify-between p-4">
				<div className="flex items-center gap-3">
					<Crown className="h-5 w-5 text-content-secondary" />
					<div>
						<h4 className="font-medium text-base">{plan.name}</h4>
						<p className="text-sm text-content-tertiary">{plan.description}</p>
					</div>
				</div>
				<Button
					variant="outline"
					className="rounded-full"
					onClick={() =>
						window.open(
							'https://wa.me/15557052438',
							'_blank',
							'noopener,noreferrer'
						)
					}
				>
					{t('account.upgrade_modal.contact_us')}
				</Button>
			</div>
		)
	}

	console.log('plan', plan)
	const isDisabled = plan.isCurrent

	return (
		<div
			className={cn(
				'p-4 rounded-lg border transition-all',
				isDisabled ? 'border-border/50 pointer-events-none' : 'cursor-pointer',
				!isDisabled && isSelected
					? 'border-main-accent bg-main-accent/5'
					: !isDisabled && 'border-border hover:border-content-tertiary'
			)}
			onClick={isDisabled ? undefined : onSelect}
		>
			<div
				className={cn(
					'flex items-start justify-between mb-2',
					isDisabled && 'opacity-60'
				)}
			>
				<div className="flex items-center gap-2">
					<h4 className="font-medium text-lg">{plan.name}</h4>
					{plan.isCurrent && (
						<Badge
							variant="secondary"
							className="text-xs bg-content-tertiary/30 text-content-tertiary rounded-sm"
						>
							{t('account.upgrade_modal.current')}
						</Badge>
					)}
				</div>
				<div className="text-right">
					<span className="text-2xl font-semibold">
						{plan.price} {plan.currency}
					</span>
					<span className="text-sm text-content-tertiary">
						{' '}
						/{t('account.upgrade_modal.zone')}
					</span>
				</div>
			</div>

			<p
				className={cn(
					'text-sm text-content-tertiary mb-3',
					isDisabled && 'opacity-60'
				)}
			>
				{plan.description}
			</p>

			<ul className={cn('space-y-1.5', isDisabled && 'opacity-60')}>
				{plan.features.map((feature, index) => (
					<li
						key={index}
						className="flex items-center gap-2 text-sm text-content-secondary"
					>
						<span className="text-content-tertiary">•</span>
						{feature}
					</li>
				))}
			</ul>
		</div>
	)
}

// Success View Component
const SuccessView: React.FC<{
	planName: string
	onClose: () => void
}> = ({ planName, onClose }) => {
	const { t } = useTranslation('common')

	return (
		<div className="px-6 py-12 flex flex-col items-center justify-center text-center">
			{/* Success Icon */}
			<div className="w-24 h-24 rounded-2xl bg-content-tertiary/20 flex items-center justify-center mb-8">
				<Check className="h-12 w-12 text-foreground" />
			</div>

			{/* Title */}
			<h2 className="text-2xl font-semibold mb-4">
				{t('account.upgrade_modal.success_title', { plan: planName })}
			</h2>

			{/* Description */}
			<p className="text-content-tertiary mb-8 max-w-md">
				{t('account.upgrade_modal.success_description')}
			</p>

			{/* Button */}
			<Button variant="outline" onClick={onClose} className="rounded-full px-8">
				{t('account.upgrade_modal.back_to_mybrandtrack')}
			</Button>
		</div>
	)
}

// Confirmation View Component
const ConfirmationView: React.FC<{
	selectedPlan: Plan
	currentPlanName: string
	zonesCount: number
	billingCycle: string
	onCancel: () => void
	onConfirm: () => void
	isConfirming?: boolean
}> = ({
	selectedPlan,
	currentPlanName,
	zonesCount,
	billingCycle,
	onCancel,
	onConfirm,
	isConfirming = false,
}) => {
	const { t } = useTranslation('common')
	const total = selectedPlan.price * zonesCount

	return (
		<>
			{/* Content */}
			<div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
				{/* Summary Card */}
				<div className="rounded-lg border border-border bg-card-dark">
					{/* Plan header */}
					<div className="p-4">
						<div className="flex items-center justify-between mb-4">
							<h4 className="font-semibold text-lg">{selectedPlan.name}</h4>
							<Badge
								variant="secondary"
								className="text-sm bg-content-tertiary/20 text-content-secondary rounded-sm"
							>
								{zonesCount} {t('account.upgrade_modal.zones')}
							</Badge>
						</div>

						<div className="flex items-start justify-between">
							<div>
								<p className="text-content-secondary">{currentPlanName}</p>
								<p className="text-sm text-content-tertiary">
									x{zonesCount} {t('account.upgrade_modal.zone')}
								</p>
							</div>
							<div className="text-right">
								<span className="text-lg font-semibold">
									{selectedPlan.price} {selectedPlan.currency}
								</span>
								<span className="text-sm text-content-tertiary">
									/{t('account.upgrade_modal.zone')}
								</span>
							</div>
						</div>
					</div>

					<Separator />

					{/* Total section */}
					<div className="p-4">
						<div className="flex items-start justify-between">
							<div>
								<p className="text-2xl font-semibold">
									{t('account.upgrade_modal.total')}
								</p>
								<p className="text-sm text-content-tertiary">
									{t('account.upgrade_modal.billed_cycle', {
										cycle: billingCycle,
									})}
								</p>
							</div>
							<div className="text-right">
								<p className="text-2xl font-semibold">
									{total} {selectedPlan.currency}
								</p>
								<p className="text-sm text-content-tertiary">
									{t('account.upgrade_modal.excluding_tax')}
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Info notice */}
				<div className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card-dark">
					<Info className="h-5 w-5 text-content-tertiary flex-shrink-0 mt-0.5" />
					<p className="text-sm text-content-secondary">
						{t('account.upgrade_modal.proration_notice')}
					</p>
				</div>
			</div>

			{/* Footer */}
			<div className="px-6 py-5 border-t flex justify-end gap-3">
				<Button
					type="button"
					variant="ghost"
					onClick={onCancel}
					disabled={isConfirming}
					className="rounded-full px-6"
				>
					{t('account.upgrade_modal.cancel')}
				</Button>
				<Button
					type="button"
					variant="default"
					onClick={onConfirm}
					disabled={isConfirming}
					className="rounded-full px-6"
				>
					{isConfirming
						? t('account.upgrade_modal.confirming')
						: t('account.upgrade_modal.confirm')}
				</Button>
			</div>
		</>
	)
}

export const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({
	visible,
	onClose,
	onUpgrade,
	currentPlanId = 'essential',
	currentPlanName = 'Brandtrack Essential',
	zonesCount = 1,
	billingCycle = 'monthly',
	subscriptionPlans = [],
	subscription,
}) => {
	const { t } = useTranslation('common')
	const [step, setStep] = useState<ModalStep>('select')
	const [selectedPlanId, setSelectedPlanId] = useState<string>('advanced')
	const [isConfirming, setIsConfirming] = useState(false)

	// Use real plans from API if available, otherwise fallback to hardcoded PLANS
	const availablePlans = useMemo(() => {
		if (subscriptionPlans.length > 0) {
			const currency = subscription?.currency || 'USD'
			// Map API plans to internal Plan format
			return subscriptionPlans
				.filter((plan) => plan.metadata?.isSelectable)
				.map((apiPlan) => ({
					id: apiPlan.name.toLowerCase(),
					name: apiPlan.name,
					description: apiPlan.description || '',
					price: apiPlan.getZonePrice(currency)?.price || 0,
					currency: currency,
					features: [], // Can be filled from metadata if available
					tier: apiPlan.metadata?.tier || 1,
					isContactSales: false,
				}))
		}
		return PLANS
	}, [subscriptionPlans])

	// Get current plan tier
	const currentPlanTier = useMemo(() => {
		const currentApiPlan = subscriptionPlans.find(
			(p) => p.name.toLowerCase() === currentPlanId?.toLowerCase()
		)
		return currentApiPlan?.metadata?.tier || 1
	}, [subscriptionPlans, currentPlanId])

	// Determine if selected plan is upgrade or downgrade
	const selectedPlanTier = useMemo(() => {
		const selectedApiPlan = subscriptionPlans.find(
			(p) => p.name.toLowerCase() === selectedPlanId?.toLowerCase()
		)
		return selectedApiPlan?.metadata?.tier || 2
	}, [subscriptionPlans, selectedPlanId])

	const isDowngrade = selectedPlanTier < currentPlanTier

	const selectedPlan = availablePlans.find((p) => p.id === selectedPlanId)
	const canChange =
		selectedPlanId !== currentPlanId && !selectedPlan?.isContactSales

	const handleClose = () => {
		setStep('select')
		setSelectedPlanId('advanced')
		setIsConfirming(false)
		onClose()
	}

	const handleProceedToConfirm = () => {
		if (canChange) {
			setStep('confirm')
		}
	}

	const handleConfirm = async () => {
		if (canChange && selectedPlan) {
			setIsConfirming(true)
			try {
				await onUpgrade(selectedPlanId)
				setIsConfirming(false)
				setStep('success')
			} catch (error) {
				console.error('Plan change failed:', error)
				setIsConfirming(false)
			}
		}
	}

	const handleBackToSelect = () => {
		setStep('select')
	}

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[600px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{step === 'success' ? (
					<SuccessView
						planName={selectedPlan?.name || 'Advanced'}
						onClose={handleClose}
					/>
				) : (
					<>
						{/* Header */}
						<div className="px-6 py-5 border-b">
							<DialogTitle className="text-xl font-semibold">
								{isDowngrade
									? t('account.downgrade_plan', {
											defaultValue: 'Downgrade Plan',
										})
									: t('account.upgrade_plan')}
							</DialogTitle>
							<DialogDescription className="text-content-tertiary mt-1">
								{isDowngrade
									? t('account.downgrade_modal.subtitle', {
											defaultValue: 'Select the plan you want to downgrade to',
										})
									: t('account.upgrade_modal.subtitle')}
							</DialogDescription>
						</div>

						{step === 'select' ? (
							<>
								{/* Plan Selection Content */}
								<div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
									{availablePlans
										.filter((plan) => !plan.isContactSales)
										.map((plan) => (
											<PlanCard
												key={plan.id}
												plan={{
													...plan,
													isCurrent: plan.id === currentPlanId,
												}}
												isSelected={selectedPlanId === plan.id}
												onSelect={() => setSelectedPlanId(plan.id)}
											/>
										))}

									{/* Signature plan - special treatment */}
									{availablePlans
										.filter((plan) => plan.isContactSales)
										.map((plan) => (
											<PlanCard
												key={plan.id}
												plan={plan}
												isSelected={false}
												onSelect={() => {}}
											/>
										))}
								</div>

								{/* Footer */}
								<div className="px-6 py-5 border-t flex flex-col lg:flex-row justify-end gap-3">
									<Button
										type="button"
										variant="ghost"
										onClick={handleClose}
										className="rounded-full px-6"
									>
										{t('account.upgrade_modal.maybe_later')}
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={handleProceedToConfirm}
										disabled={!canChange}
										className="rounded-full px-6"
									>
										{isDowngrade
											? t('account.downgrade_modal.downgrade_to', {
													plan: selectedPlan?.name || 'Advanced',
													defaultValue: `Downgrade to ${selectedPlan?.name || 'Advanced'}`,
												})
											: t('account.upgrade_modal.upgrade_to', {
													plan: selectedPlan?.name || 'Advanced',
												})}
									</Button>
								</div>
							</>
						) : (
							<ConfirmationView
								selectedPlan={selectedPlan!}
								currentPlanName={currentPlanName}
								zonesCount={zonesCount}
								billingCycle={billingCycle}
								onCancel={handleBackToSelect}
								onConfirm={handleConfirm}
								isConfirming={isConfirming}
							/>
						)}
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default UpgradePlanModal
